import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { PaymentReviewItem, PaymentService } from '../services/payment.service';

type PaymentRow = Omit<
  PaymentReviewItem,
  | 'pricePerPerson'
  | 'discount'
  | 'bookingTotal'
  | 'estimatedExpense'
  | 'amount'
  | 'creditCardFeeAmount'
  | 'withholdingTaxAmount'
> & {
  pricePerPerson: number;
  discount: number;
  bookingTotal: number;
  estimatedExpense: number;
  amount: number;
  creditCardFeeAmount: number;
  withholdingTaxAmount: number;
};

@Component({
  selector: 'app-payment-review-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    TableModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
  ],
  templateUrl: './payment-review-page.component.html',
})
export class PaymentReviewPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly paymentService = inject(PaymentService);
  readonly payments = signal<PaymentRow[]>([]);
  readonly isLoadingPayments = signal(false);
  readonly selectedPayment = signal<PaymentRow | null>(null);
  readonly receiptUrlError = signal('');
  readonly quotationUrlError = signal('');
  readonly statusOptions = [
    { label: 'รอตรวจสอบ', value: 'WAITING_VERIFY' },
    { label: 'ตรวจสอบแล้ว', value: 'VERIFIED' },
    { label: 'ไม่ผ่าน', value: 'REJECTED' },
  ];
  selectedStatus: string | null = null;
  selectedDate: Date | null = null;

  ngOnInit(): void {
    void this.loadPayments();
  }

  async loadPayments(): Promise<void> {
    if (this.isLoadingPayments()) return;
    this.isLoadingPayments.set(true);
    this.overlay.showLoading('กำลังโหลดรายการชำระเงิน...');
    try {
      const payments = await this.paymentService.getWaitingReviews();
      this.payments.set(
        payments.map((payment) => ({
          ...payment,
          pricePerPerson: Number(payment.pricePerPerson),
          discount: Number(payment.discount),
          bookingTotal: Number(payment.bookingTotal),
          estimatedExpense: Number(payment.estimatedExpense),
          amount: Number(payment.amount),
          creditCardFeeAmount: Number(payment.creditCardFeeAmount),
          withholdingTaxAmount: Number(payment.withholdingTaxAmount),
        })),
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดรายการชำระเงินไม่สำเร็จ', message);
    } finally {
      this.isLoadingPayments.set(false);
      this.overlay.hideLoading();
    }
  }

  openAttachment(fileUrl: string | null): void {
    if (!fileUrl) return;
    window.open(this.paymentService.resolveFileUrl(fileUrl), '_blank', 'noopener,noreferrer');
  }

  paymentTypeLabel(paymentType: string): string {
    const labels: Record<string, string> = {
      BANK_TRANSFER: 'โอนเงินผ่านธนาคาร',
      CASH: 'เงินสด',
      CREDIT_CARD: 'บัตรเครดิต/เดบิต',
    };
    return labels[paymentType] ?? paymentType ?? '—';
  }

  toDateFilter(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  openPaymentDetail(row: PaymentRow): void {
    this.clearDocumentErrors();
    this.selectedPayment.set(row);
  }

  estimatedProfit(row: PaymentRow): number {
    return row.bookingTotal - row.estimatedExpense;
  }

  totalTourPrice(row: PaymentRow): number {
    return row.pricePerPerson * row.travelerCount;
  }

  totalDiscount(row: PaymentRow): number {
    return row.discount;
  }

  closePaymentDetail(): void {
    this.selectedPayment.set(null);
  }

  async approvePayment(row: PaymentRow): Promise<void> {
    if (!this.validateDocumentUrls(row)) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันยอดชำระ',
      message: `ยืนยันยอด ${row.amount.toLocaleString()} บาท ของ ${row.bookingCode} หรือไม่`,
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังอนุมัติการชำระเงิน...');
    try {
      await this.paymentService.approve(
        row.id,
        row.receiptUrl.trim(),
        row.quotationUrl.trim(),
      );
      this.payments.update((payments) => payments.filter((payment) => payment.id !== row.id));
      this.closePaymentDetail();
      this.overlay.success('อนุมัติการชำระเงินสำเร็จ');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาตรวจสอบข้อมูลแล้วลองใหม่อีกครั้ง';
      this.overlay.error('อนุมัติไม่สำเร็จ', message);
    }
  }

  clearReceiptUrlError(): void {
    this.receiptUrlError.set('');
  }

  clearQuotationUrlError(): void {
    this.quotationUrlError.set('');
  }

  private validateDocumentUrls(row: PaymentRow): boolean {
    this.receiptUrlError.set(this.validateUrl(row.receiptUrl, 'ใบเสร็จ'));
    this.quotationUrlError.set(this.validateUrl(row.quotationUrl, 'ใบเสนอราคา'));
    return !this.receiptUrlError() && !this.quotationUrlError();
  }

  private validateUrl(value: string, label: string): string {
    if (!value.trim()) return `กรุณากรอกลิงก์${label}`;
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:'
        ? ''
        : `ลิงก์${label}ต้องขึ้นต้นด้วย http:// หรือ https://`;
    } catch {
      return `รูปแบบลิงก์${label}ไม่ถูกต้อง`;
    }
  }

  private clearDocumentErrors(): void {
    this.receiptUrlError.set('');
    this.quotationUrlError.set('');
  }

  async rejectPayment(row: PaymentRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ไม่อนุมัติการชำระเงิน',
      message: `ยืนยันไม่อนุมัติยอด ${row.amount.toLocaleString()} บาท ของ ${row.bookingCode} หรือไม่`,
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังบันทึกผลการตรวจสอบ...');
    try {
      await this.paymentService.reject(row.id);
      this.payments.update((payments) => payments.filter((payment) => payment.id !== row.id));
      this.closePaymentDetail();
      this.overlay.success('บันทึกการไม่อนุมัติเรียบร้อยแล้ว');
    } catch {
      this.overlay.error('บันทึกไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    }
  }
}
